import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  let url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
    if (input !== undefined) {
      url = `${url}?input=${encodeURIComponent(JSON.stringify(input))}`
    }
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export type MistakeStatus = "unreviewed" | "reviewed" | "mastered"

export interface MistakeRecordListItem {
  id: string
  questionId: string
  subject?: string | null
  questionType?: string | null
  knowledgePointIds: string[]
  status: MistakeStatus
  wrongCount: number
  lastWrongAt?: string | null
  questionStem?: string | null
  difficulty?: number | null
  groupId?: string | null
  groupType?: string | null
  groupPassage?: string | null
  indexInGroup?: number | null
}

export interface MistakeAttemptItem {
  id: string
  answeredAt?: string | null
  userAnswer?: Record<string, any> | null
  isCorrect?: boolean | null
  answerDurationSec?: number | null
  errorReasons?: string[]
  disputeStatus?: string | null
  disputeReason?: string | null
}

export interface MistakeRecordDetail {
  id: string
  questionId: string
  subject?: string | null
  questionType?: string | null
  knowledgePointIds: string[]
  status: MistakeStatus
  wrongCount: number
  firstWrongAt?: string | null
  lastWrongAt?: string | null
  remark?: string | null
  correctAnswer?: Record<string, any> | null
  explanation?: string | null
  questionContent?: Record<string, any> | null
  questionOptions?:
    | { label?: string; content?: string; [key: string]: any }[]
    | null
  difficulty?: number | null
  groupId?: string | null
  groupType?: string | null
  groupPassage?: string | null
  indexInGroup?: number | null
  attempts: MistakeAttemptItem[]
  attemptsTotal: number
}

export interface MistakeListResponse {
  items: MistakeRecordListItem[]
  total: number
  page: number
  pageSize: number
}

export async function fetchMistakes(params: {
  page?: number
  pageSize?: number
  subject?: string
  questionType?: string
  knowledgePointId?: string
  status?: MistakeStatus
  keyword?: string
  includeMastered?: boolean
  hasDispute?: boolean
}): Promise<MistakeListResponse> {
  return tRPCRequest<MistakeListResponse>("mistakes.list", params, "GET")
}

export async function fetchDeletedMistakes(params: {
  page?: number
  pageSize?: number
}): Promise<MistakeListResponse> {
  return tRPCRequest<MistakeListResponse>("mistakes.listDeleted", params, "GET")
}

export async function fetchMistakeDetail(
  mistakeId: string,
  params?: { attemptsPage?: number; attemptsPageSize?: number },
): Promise<{ record: MistakeRecordDetail }> {
  return tRPCRequest<{ record: MistakeRecordDetail }>(
    "mistakes.getDetail",
    { recordId: mistakeId, ...params },
    "GET",
  )
}

export async function updateMistake(
  mistakeId: string,
  payload: { status?: MistakeStatus; remark?: string; errorReasons?: string[] },
): Promise<{ record: MistakeRecordDetail }> {
  return tRPCRequest<{ record: MistakeRecordDetail }>("mistakes.update", {
    recordId: mistakeId,
    ...payload,
  })
}

export async function createMistakeAttempt(
  mistakeId: string,
  payload: {
    userAnswer?: Record<string, any> | null
    isCorrect: boolean
    answerDurationSec?: number | null
  },
): Promise<{ record: MistakeRecordDetail; suggestedMastered: boolean }> {
  return tRPCRequest("mistakes.createAttempt", {
    recordId: mistakeId,
    ...payload,
  })
}

export async function deleteMistake(mistakeId: string): Promise<void> {
  return tRPCRequest<void>("mistakes.softDelete", { recordId: mistakeId })
}

export async function restoreMistake(mistakeId: string): Promise<void> {
  return tRPCRequest<void>("mistakes.restore", { recordId: mistakeId })
}

export async function fetchQuestionTypes(): Promise<string[]> {
  return tRPCRequest<string[]>("mistakes.questionTypes")
}

export async function disputeAttempt(
  recordId: string,
  attemptId: string,
  reason: string,
): Promise<{ record: MistakeRecordDetail }> {
  return tRPCRequest("mistakes.dispute", { recordId, attemptId, reason })
}

// ─── Export ─────────────────────────────────────────────────────

export interface ExportParams {
  ids?: string[]
  subject?: string
  questionType?: string
  status?: string
  startTime?: string
  endTime?: string
  keyword?: string
  includeMastered?: boolean
  hideAnswer?: boolean
}

function buildExportQuery(params: ExportParams): string {
  const searchParams = new URLSearchParams()
  if (params.ids && params.ids.length > 0) {
    searchParams.set('ids', params.ids.join(','))
  }
  if (params.subject) searchParams.set('subject', params.subject)
  if (params.questionType) searchParams.set('questionType', params.questionType)
  if (params.status) searchParams.set('status', params.status)
  if (params.startTime) searchParams.set('startTime', params.startTime)
  if (params.endTime) searchParams.set('endTime', params.endTime)
  if (params.keyword) searchParams.set('keyword', params.keyword)
  if (params.includeMastered) searchParams.set('includeMastered', 'true')
  if (params.hideAnswer) searchParams.set('hideAnswer', 'true')
  return searchParams.toString()
}

/**
 * Download mistakes as Excel file (.xlsx)
 */
export async function downloadExcel(params: ExportParams): Promise<void> {
  const token = localStorage.getItem(STORAGE_KEY)
  const query = buildExportQuery(params)
  const url = `${API_HOST}/api/v1/mistakes/export/excel?${query}`

  const response = await fetch(url, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error((data as any)?.error || '导出失败')
  }

  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  const dateStr = new Date().toISOString().slice(0, 10)
  link.download = `错题本_${dateStr}.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(downloadUrl)
}

/**
 * Download mistakes PDF export (Typst-generated PDF file)
 */
export async function openPdfExport(params: ExportParams): Promise<void> {
  const token = localStorage.getItem(STORAGE_KEY)
  const query = buildExportQuery(params)
  const url = `${API_HOST}/api/v1/mistakes/export/pdf?${query}`

  const response = await fetch(url, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error((data as any)?.error || '导出失败')
  }

  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  const dateStr = new Date().toISOString().slice(0, 10)
  link.download = `错题本_${dateStr}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(downloadUrl)
}

// ─── 微课课堂练习答题上报 ─────────────────────────────

export interface MicroQuizReportInput {
  lessonId: string
  elementId: string
  slideId?: string
  lessonTitle?: string
  subject?: string
  /** quiz 内容（与题库 questions 对齐的新结构） */
  quiz: {
    type: 'choice' | 'multi_choice' | 'fill_blank' | 'true_false'
    stem: string
    images?: string[]
    options?: { label: string; content: string; images?: string[] }[]
    answer: { value: string }
    analysis?: string
  }
  /** 学生提交的答案（与题库 answer 格式一致） */
  userAnswer: { value: string }
  isCorrect: boolean
}

/**
 * 上报微课课堂练习答题结果（答错时写入错题本）
 */
export async function reportMicroQuiz(input: MicroQuizReportInput) {
  return tRPCRequest<{ created: boolean; suggestedMastered: boolean }>(
    "mistakes.recordMicroQuiz",
    input,
    "POST",
  )
}
